import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, CircularProgress, FormControl, IconButton, InputLabel,
  MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { SalesInvoiceService } from '../../services/salesInvoice.service';
import {
  SalesInvoice, SalesInvoiceLineCreateDTO, SalesInvoiceEditDTO,
} from '../../interfaces/sales/salesInvoice.interface';
import { CustomerService } from '../../services/customer.service';
import { Customer } from '../../interfaces/sales/customer.interface';
import { AccountService } from '../../services/account.service';
import { Account, Segment } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { SegmentService } from '../../services/segment.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';

// In-state row model used by both branches. quantity/unitPrice/taxRatePct may be
// NaN while a numeric field is being typed/cleared; `key` is a stable client key
// that is never sent to the server. For the restricted (ISSUED) branch the
// existing backend line id is carried in `lineId`.
interface LineRow extends Omit<SalesInvoiceLineCreateDTO, 'quantity' | 'unitPrice' | 'taxRatePct'> {
  key: string;
  lineId?: string;
  quantity: number;   // may be NaN while field is empty
  unitPrice: number;  // may be NaN while field is empty
  taxRatePct: number; // may be NaN while field is empty
}

// Helpers that treat NaN as 0 for live-total computation only. Same formulas as
// SalesInvoiceCreate.
const safeLineNet = (row: LineRow): number => {
  const q = Number.isFinite(row.quantity) ? row.quantity : 0;
  const p = Number.isFinite(row.unitPrice) ? row.unitPrice : 0;
  return +((q * p)).toFixed(4);
};
const safeLineTax = (row: LineRow): number => {
  const t = Number.isFinite(row.taxRatePct) ? row.taxRatePct : 0;
  return +((safeLineNet(row) * t) / 100).toFixed(4);
};

const SalesInvoiceEdit: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();

  // Stable key counter — bumped each time a new row is created.
  const keyCounter = useRef(0);
  const nextKey = () => String(++keyCounter.current);

  // Loaded invoice (drives which branch renders)
  const [invoice, setInvoice] = useState<SalesInvoice | null>(null);

  // Header state
  const [customerId, setCustomerId] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [editReason, setEditReason] = useState('');
  const [editReasonTouched, setEditReasonTouched] = useState(false);

  // Reference data (only needed for the DRAFT full-edit branch)
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [revenueAccounts, setRevenueAccounts] = useState<Account[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);

  // Lines — stored as LineRow[] (includes transient key)
  const [lines, setLines] = useState<LineRow[]>([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    const institutionId = AuthService.getSelectedInstitution()!;
    Promise.all([
      SalesInvoiceService.getById(id),
      CustomerService.listByInstitution(institutionId, 0, 200),
      AccountService.getAccountsByType('REVENUE'),
      SegmentService.getByInstitution(0, 200),
    ])
      .then(([inv, customerPage, revenueAccts, segmentPage]: [
        SalesInvoice, { content: Customer[] }, Account[], { content?: Segment[] },
      ]) => {
        setInvoice(inv);
        setCustomerId(inv.customerId);
        setIssueDate(inv.issueDate);
        setDueDate(inv.dueDate);
        setNotes(inv.notes ?? '');

        setCustomers(customerPage.content);
        setRevenueAccounts(Array.isArray(revenueAccts) ? revenueAccts : []);
        const allSegments: Segment[] = segmentPage.content ?? [];
        setSegments(allSegments.filter(s => s.active !== false));

        // Prefill lines from the invoice. For DRAFT we still carry lineId
        // harmlessly; for ISSUED/PARTIALLY_PAID we need it for the edit DTO.
        setLines((inv.lines ?? []).map(l => ({
          key: nextKey(),
          lineId: l.id,
          description: l.description,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          taxRatePct: l.taxRatePct,
          revenueAccountId: l.revenueAccountId || undefined,
          segmentId: l.segmentId || undefined,
        })));
      })
      .catch(e => toast.showApiError(e, 'Failed to load invoice'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddLine = () => {
    setLines(prev => [...prev, {
      key: nextKey(),
      description: '',
      quantity: 1,
      unitPrice: NaN,
      taxRatePct: 0,
      revenueAccountId: undefined,
      segmentId: undefined,
    }]);
  };

  const handleRemoveLine = (index: number) => {
    setLines(prev => prev.filter((_, i) => i !== index));
  };

  const handleLineChange = <K extends keyof LineRow>(
    index: number,
    field: K,
    value: LineRow[K]
  ) => {
    setLines(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Live totals — NaN fields treated as 0.
  const subtotal = lines.reduce((s, l) => s + safeLineNet(l), 0);
  const taxAmount = lines.reduce((s, l) => s + safeLineTax(l), 0);
  const total = subtotal + taxAmount;

  const status = invoice?.status;
  const isDraft = status === 'DRAFT';
  const isRestricted = status === 'ISSUED' || status === 'PARTIALLY_PAID';

  const draftSubmitDisabled =
    saving ||
    !customerId ||
    lines.length === 0 ||
    lines.some(l => !(l.quantity > 0) || !(l.unitPrice > 0));

  const restrictedSubmitDisabled =
    saving ||
    !editReason.trim() ||
    lines.some(l => !(l.unitPrice > 0));

  // DRAFT → full replacement via update(id, SalesInvoiceCreateDTO)
  const handleDraftSubmit = async () => {
    if (!id) return;
    if (!customerId) { toast.showApiError(null, 'Select a customer'); return; }
    if (lines.length === 0) { toast.showApiError(null, 'Add at least one line'); return; }
    if (lines.some(l => !(l.quantity > 0) || !(l.unitPrice > 0))) {
      toast.showApiError(null, 'All lines must have a positive quantity and unit price');
      return;
    }

    setSaving(true);
    try {
      const institutionId = AuthService.getSelectedInstitution()!;
      const dto = {
        customerId,
        issueDate,
        dueDate,
        notes: notes || undefined,
        institutionId,
        lines: lines.map(l => ({
          description: l.description,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          taxRatePct: Number.isFinite(l.taxRatePct) ? l.taxRatePct : 0,
          revenueAccountId: l.revenueAccountId || undefined,
          segmentId: l.segmentId || undefined,
        })),
      };
      await SalesInvoiceService.update(id, dto);
      toast.showSuccess('Sales Invoice updated');
      navigate(`/sales/invoices/${id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to update Sales Invoice');
    } finally {
      setSaving(false);
    }
  };

  // ISSUED/PARTIALLY_PAID → audit-trailed correction via edit(id, SalesInvoiceEditDTO)
  const handleRestrictedSubmit = async () => {
    if (!id) return;
    if (!editReason.trim()) { toast.showApiError(null, 'Edit reason is required'); return; }
    if (lines.some(l => !(l.unitPrice > 0))) {
      toast.showApiError(null, 'All lines must have a positive unit price');
      return;
    }
    if (lines.some(l => !l.lineId)) {
      toast.showApiError(null, 'Internal error: a line is missing its id');
      return;
    }

    setSaving(true);
    try {
      const dto: SalesInvoiceEditDTO = {
        editReason: editReason.trim(),
        issueDate,
        dueDate,
        notes: notes || undefined,
        lines: lines.map(l => ({
          lineId: l.lineId!,
          unitPrice: l.unitPrice,
          taxRatePct: Number.isFinite(l.taxRatePct) ? l.taxRatePct : 0,
          description: l.description,
        })),
      };
      await SalesInvoiceService.edit(id, dto);
      toast.showSuccess('Sales Invoice updated');
      navigate(`/sales/invoices/${id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to update Sales Invoice');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  if (!invoice) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Invoice not found.</Typography>
        <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
      </Box>
    );
  }

  // PAID or VOIDED → not editable
  if (!isDraft && !isRestricted) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Edit Sales Invoice {invoice.invoiceNumber ?? ''}
        </Typography>
        <Typography sx={{ mb: 3 }} color="text.secondary">
          This invoice can no longer be edited.
        </Typography>
        <Button variant="outlined" onClick={() => navigate(`/sales/invoices/${invoice.id}`)}>
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Edit Sales Invoice {invoice.invoiceNumber ?? ''}
        {isRestricted && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            (restricted edit — {status})
          </Typography>
        )}
      </Typography>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {isDraft ? (
            <TextField
              select
              label="Customer"
              value={customerId}
              onChange={e => setCustomerId(e.target.value)}
              required
            >
              {customers.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              label="Customer"
              value={invoice.customerName ?? customerId}
              InputProps={{ readOnly: true }}
              disabled
            />
          )}

          <TextField
            label="Issue Date"
            type="date"
            value={issueDate}
            onChange={e => setIssueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />

          <TextField
            label="Due Date"
            type="date"
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />

          <TextField
            label="Notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            multiline
            minRows={3}
          />

          {isRestricted && (
            <TextField
              label="Edit Reason"
              value={editReason}
              onChange={e => setEditReason(e.target.value)}
              onBlur={() => setEditReasonTouched(true)}
              required
              error={editReasonTouched && !editReason.trim()}
              helperText="Required — recorded in the invoice audit trail"
              multiline
              minRows={2}
            />
          )}
        </Box>
      </Paper>

      {/* Lines */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Invoice Lines</Typography>
          {isDraft && (
            <Button variant="outlined" startIcon={<Add />} onClick={handleAddLine}>
              Add Line
            </Button>
          )}
        </Box>

        {lines.length === 0 ? (
          <Typography color="text.secondary">No lines.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Unit Price</TableCell>
                <TableCell>Tax %</TableCell>
                {isDraft && <TableCell>Revenue Account</TableCell>}
                {isDraft && <TableCell>Segment</TableCell>}
                <TableCell align="right">Net</TableCell>
                <TableCell align="right">Tax</TableCell>
                {isDraft && <TableCell></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line, index) => (
                <TableRow key={line.key}>
                  <TableCell>
                    <TextField
                      size="small"
                      value={line.description}
                      onChange={e => handleLineChange(index, 'description', e.target.value)}
                      placeholder="Description"
                      sx={{ minWidth: 160 }}
                    />
                  </TableCell>
                  <TableCell>
                    {isDraft ? (
                      <TextField
                        type="number"
                        size="small"
                        value={Number.isFinite(line.quantity) ? line.quantity : ''}
                        onChange={e => {
                          const v = e.target.value;
                          handleLineChange(index, 'quantity', v === '' ? NaN : Number(v));
                        }}
                        error={!(line.quantity > 0)}
                        inputProps={{ step: 1, min: 0 }}
                        sx={{ width: 90 }}
                      />
                    ) : (
                      // Restricted: quantity is read-only.
                      <Typography variant="body2">{line.quantity}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={Number.isFinite(line.unitPrice) ? line.unitPrice : ''}
                      onChange={e => {
                        const v = e.target.value;
                        handleLineChange(index, 'unitPrice', v === '' ? NaN : Number(v));
                      }}
                      error={!(line.unitPrice > 0)}
                      inputProps={{ step: 0.0001, min: 0 }}
                      sx={{ width: 110 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={Number.isFinite(line.taxRatePct) ? line.taxRatePct : ''}
                      onChange={e => {
                        const v = e.target.value;
                        handleLineChange(index, 'taxRatePct', v === '' ? NaN : Number(v));
                      }}
                      inputProps={{ step: 0.01, min: 0 }}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  {isDraft && (
                    <TableCell>
                      <Box sx={{ minWidth: 180 }}>
                        <AccountAutocomplete
                          accounts={revenueAccounts}
                          value={line.revenueAccountId ?? ''}
                          label="Revenue Account"
                          placeholder="—"
                          onChange={accountId => {
                            handleLineChange(index, 'revenueAccountId', accountId || undefined);
                          }}
                        />
                      </Box>
                    </TableCell>
                  )}
                  {isDraft && (
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Segment</InputLabel>
                        <Select
                          label="Segment"
                          value={line.segmentId ?? ''}
                          onChange={e => {
                            const val = e.target.value as string;
                            handleLineChange(index, 'segmentId', val || undefined);
                          }}
                        >
                          <MenuItem value="">—</MenuItem>
                          {segments.map(s => (
                            <MenuItem key={s.id} value={s.id!}>{s.code} — {s.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                  )}
                  <TableCell align="right">{safeLineNet(line).toFixed(4)}</TableCell>
                  <TableCell align="right">{safeLineTax(line).toFixed(4)}</TableCell>
                  {isDraft && (
                    <TableCell>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveLine(index)}
                        aria-label="remove line"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Totals */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Subtotal</Typography>
            <Typography variant="body1">{subtotal.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Tax</Typography>
            <Typography variant="body1">{taxAmount.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Total</Typography>
            <Typography variant="h6">{total.toFixed(2)}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={isDraft ? handleDraftSubmit : handleRestrictedSubmit}
          disabled={isDraft ? draftSubmitDisabled : restrictedSubmitDisabled}
        >
          {saving ? <CircularProgress size={20} /> : 'Save Changes'}
        </Button>
        <Button variant="outlined" onClick={() => navigate(-1)} disabled={saving}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default SalesInvoiceEdit;
